import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, FileSpreadsheet, Loader2, Map } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGoBack } from "@/lib/useGoBack";
import { HomeButton } from "@/components/HomeButton";
import { ExportDestinationDialog } from "@/components/ExportDestinationDialog";
import { CLOUD_EXPORT_LABELS, uploadExportToCloud, saveBlobToDevice, formExportFilename } from "@/lib/cloudExports";
import { useASEFormData } from "@/components/ase/useASEFormData";
import { SheetGrid } from "@/components/aseClassic/SheetGrid";
import { evaluateFormula, fmt } from "@/components/aseClassic/formula";
import { buildSowNotesText } from "@/components/ase/ASESOWNotesTab";

const SOW_CELL = "SVCL Output!E2"; // Sow Notes — shared with ASE Form Houston's SOW Notes tab
// SVCL Site & Order cells read/write the same answers as ASE Form Houston's SVCL tab
const SHARED_CELLS = {
  "SVCL Output!B2": { key: "building_contact", value: (f) => f.building_contact || f.building_owner_contact || "" },
  "SVCL Output!B4": { key: "building_contact_email", value: (f) => f.building_contact_email || "" },
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

// Cell lookup across the workbook: answers from the shared ASE data, guide text from the template, formulas evaluated live
const useWorkbookValues = (layout, formData) => useMemo(() => {
  if (!layout) return () => "";
  const byName = Object.fromEntries(layout.sheets.map((s) => [s.name, s]));
  const cache = {};
  const raw = (sheetName, ref, depth = 0) => {
    const sh = byName[sheetName];
    if (!sh && !layout.refs[sheetName]) return "";
    const key = sh?.editable ? sh.fields[ref] : null;
    if (key && formData[key]) return formData[key];
    if (`${sheetName}!${ref}` === SOW_CELL) return formData.sow_notes_override || buildSowNotesText(formData);
    const shared = SHARED_CELLS[`${sheetName}!${ref}`];
    if (shared) return formData.classic_overrides?.[`${sheetName}!${ref}`] || shared.value(formData);
    const ov = formData.classic_overrides?.[`${sheetName}!${ref}`];
    if (sh && !sh.editable && ov) return ov;
    const v = sh ? sh.cells[ref]?.v : layout.refs[sheetName][ref];
    if (v == null) return "";
    if (!v.startsWith("=") || depth > 20) return v;
    const ck = `${sheetName}!${ref}`;
    if (!(ck in cache)) cache[ck] = evaluateFormula(v, (s, r) => raw(s || sheetName, r, depth + 1));
    return cache[ck];
  };
  return (sheetName, ref) => fmt(raw(sheetName, ref));
}, [layout, formData]);

const ASEFormClassic = () => {
  const { jobId } = useParams();
  const goBack = useGoBack(`/jobs/${jobId}`);
  const { job, loading, saving, formData, handleChange, handleSave } = useASEFormData(jobId);
  const [layout, setLayout] = useState(null);
  const [pendingExport, setPendingExport] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const valueOf = useWorkbookValues(layout, formData);
  // Output-sheet edits are saved with the shared ASE answers; clearing a cell returns it to its formula
  const setOverride = (sheetName, ref, value) => {
    if (`${sheetName}!${ref}` === SOW_CELL) return handleChange("sow_notes_override", value);
    if (SHARED_CELLS[`${sheetName}!${ref}`]) return handleChange(SHARED_CELLS[`${sheetName}!${ref}`].key, value);
    const next = { ...(formData.classic_overrides || {}) };
    if (value === "") delete next[`${sheetName}!${ref}`];
    else next[`${sheetName}!${ref}`] = value;
    handleChange("classic_overrides", next);
  };

  useEffect(() => {
    axios.get("/ase-classic/layout").then((r) => setLayout(r.data)).catch(() => toast.error("Could not load the Classic form layout"));
  }, []);

  const runExport = async (dest) => {
    setExportBusy(true);
    try {
      await axios.put(`/jobs/${jobId}/ase-data`, { ase_data: formData });
      const r = await axios.get(`/jobs/${jobId}/export/ase-classic`, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const filename = formExportFilename(job, "ASE Checklist Classic", "xlsx");
      if (dest === "device") { saveBlobToDevice(blob, filename); toast.success("Downloaded to device"); }
      else { await uploadExportToCloud(jobId, "ase_classic_excel", filename, blob); toast.success("Saved to app cloud — see Cloud Exports on the job page"); }
      setPendingExport(false);
    } catch {
      toast.error(dest === "cloud" ? "Cloud save failed" : "Export failed");
    } finally {
      setExportBusy(false);
    }
  };

  if (loading || !layout) {
    return <div className="min-h-screen bg-zinc-100 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-100">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" onClick={goBack} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="ase-classic-back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <HomeButton />
            <div className="min-w-0">
              <h1 className="font-heading text-lg sm:text-xl font-bold truncate">ASE FORM CLASSIC</h1>
              <p className="text-xs text-zinc-500 font-mono truncate" data-testid="ase-classic-job">{job?.job_type || "OCALC"}{job?.site_id ? ` • ${job.site_id}` : ""} · answers shared with ASE Form Houston</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Link to={`/jobs/${jobId}/map`}>
              <Button variant="outline" size="sm" className="bg-green-50 border-green-300 hover:bg-green-100 text-green-700" data-testid="ase-classic-map-btn"><Map className="w-4 h-4" /><span className="hidden sm:inline ml-1">Map</span></Button>
            </Link>
            <Button onClick={handleSave} disabled={saving} variant="outline" size="sm" data-testid="ase-classic-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}<span className="hidden sm:inline ml-1">Save</span>
            </Button>
            <Button onClick={() => setPendingExport(true)} disabled={exportBusy} size="sm" data-testid="ase-classic-export-btn">
              {exportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}<span className="hidden sm:inline ml-1">Export</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-2 sm:px-6 py-5" data-testid="ase-classic-form">
        <Tabs defaultValue={slug(layout.sheets[0].name)}>
          <TabsList className="mb-3 flex flex-wrap h-auto justify-start gap-1 bg-zinc-200 p-1 rounded-md">
            {layout.sheets.map((s) => (
              <TabsTrigger key={s.name} value={slug(s.name)} className="text-xs sm:text-sm data-[state=active]:bg-white" data-testid={`ase-classic-tab-${slug(s.name)}`}>
                {s.name}
              </TabsTrigger>
            ))}
          </TabsList>
          {layout.sheets.map((s) => (
            <TabsContent key={s.name} value={slug(s.name)} className="space-y-2">
              {!s.editable && (
                <p className="text-xs text-zinc-600" data-testid={`ase-classic-output-note-${slug(s.name)}`}>
                  Fills itself from your ASE CKLIST answers like the Excel formulas — type in any value cell to override it; clear the cell to go back to the formula.{s.hidden_in_excel ? " (This sheet is hidden in the Excel file.)" : ""}
                </p>
              )}
              <SheetGrid sheet={s} formData={formData} onChange={handleChange} valueOf={(ref) => valueOf(s.name, ref)} onOverride={setOverride} extraEditable={Object.keys(SHARED_CELLS).filter((k) => k.startsWith(`${s.name}!`)).map((k) => k.split("!")[1])} />
            </TabsContent>
          ))}
        </Tabs>
      </main>
      <ExportDestinationDialog open={pendingExport} onOpenChange={(v) => { if (!v) setPendingExport(false); }} label={CLOUD_EXPORT_LABELS.ase_classic_excel}
        busy={exportBusy} onDevice={() => runExport("device")} onCloud={() => runExport("cloud")} />
    </div>
  );
};

export default ASEFormClassic;
